/**
 * `cssVarConfig` + `antdTheme` — la config que consume el ConfigProvider.
 *
 * Extraído de `theme-silia.ts` (paso 03, ver `theme/spec.md` §9).
 */

import type { ThemeConfig } from 'antd';
import { antdToken } from './tokens';
import { antdComponents } from './components';

/**
 * CSS-variable mode.
 *
 * Without it AntD compiles every token into the rules it generates, so each
 * component ships its own copy of the palette and switching brand means handing
 * ConfigProvider a new object and re-rendering the tree. With it, AntD emits its
 * generated CSS as `var(--silia-*)` references plus ONE block declaring them —
 * so a brand switch is a rewrite of that block, and nothing re-renders.
 *
 * The prefix is `silia`, not the default `ant`, for two reasons. It puts AntD's
 * ~400 generated variables in the same namespace as the tokens above, so the
 * whole platform reads from one place. And it keeps them clear of the 159
 * `--ant-*` aliases this file still emits for the migration: measured today
 * those two sets do not overlap, but AntD generates generic names (`--ant-blue`,
 * `--ant-purple`, `--ant-green`) that a future hand-written alias could easily
 * collide with. Different prefix, no such race.
 *
 * Note the two `--silia-*` sets are distinguishable on sight: AntD generates
 * kebab-case (`--silia-color-primary`), this file writes the semantic ones
 * (`--silia-ink-strong`, `--silia-shadow-md`). Never hand-write a kebab-case
 * `--silia-color-*` — that space belongs to AntD.
 *
 * `key` is set explicitly: AntD needs it to scope the variables when React is
 * older than 18, and pinning it keeps the generated class stable across builds.
 */
export const cssVarConfig = { prefix: 'silia', key: 'silia' } as const;

export const antdTheme: ThemeConfig = {
  token: antdToken,
  components: antdComponents,
  cssVar: cssVarConfig,
  /** Only one AntD version in the app, so the style hash is dead weight. */
  hashed: false,
};
